import { useMemo } from 'react'
import { useCityStore } from '@/store/cityStore'
import { useFilterOptions } from '@/hooks/api/useFilterOptions'
import { useMenuCounts } from '@/hooks/api/useMenuCounts'
import { areaFilterValue, areasForCity } from '@/lib/areaFilterUtils'
import { PATHS } from '@/routes/paths'
import type { MegaMenuColumn, MegaMenuConfig, MegaMenuLink, MegaMenuSection } from '@/lib/megaMenu'
import {
  buildPropertyHref,
  splitCategories,
  titleCase,
  type CategoryLike,
} from '@/lib/propertyNavLinks'

type Intent = 'buy' | 'rent'
type CountMap = Record<string, number>

const MENU_BHK_RE = /^([1-4])\s*BHK$/i

/** Mirrors the listing's category filter (`title LIKE %name%`). */
function categoryCount(map: CountMap, name: string): number {
  const needle = name.trim().toLowerCase()
  if (!needle) return 0
  return Object.entries(map).reduce(
    (sum, [title, n]) => (title.toLowerCase().includes(needle) ? sum + n : sum),
    0,
  )
}

/** Keeps only items with results, highest count first. */
function topWithResults<T>(items: readonly T[], count: (item: T) => number, limit: number): T[] {
  return items
    .map((item) => ({ item, n: count(item) }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, limit)
    .map((x) => x.item)
}

/**
 * Builds the "For Buyers" / "For Tenants" mega menu from live listing counts for
 * the selected city. A link is only shown when it leads to at least one active,
 * visible property; empty columns and sections are dropped.
 *
 * Until `/menu-counts` answers, per-city counts from filter-options are used and
 * links that cannot be verified that way (BHK, verified) stay hidden.
 */
export function useMegaMenu(intent: Intent): MegaMenuConfig {
  const cityName = useCityStore((s) => s.cityName)
  const { options } = useFilterOptions(cityName)
  const { data: menuCounts } = useMenuCounts(cityName)

  return useMemo<MegaMenuConfig>(() => {
    const city = cityName?.trim() || 'your city'
    const cityLower = city.toLowerCase()
    const isRent = intent === 'rent'
    const live = menuCounts?.[intent]
    const commercialLive = menuCounts?.commercial

    const countIn = (map: CountMap | undefined) => (c: CategoryLike) =>
      map ? categoryCount(map, c.name) : (c.count ?? 0)
    const intentCategoryCount = countIn(live?.categories)

    const { land, commercial, residential } = splitCategories(
      (options.categories ?? []).filter((c) => (c.count ?? 0) > 0),
    )

    const cityTotal = live
      ? live.total
      : (options.cities ?? []).find((c) => c.name.toLowerCase() === cityLower)?.count ?? 0

    const sections: MegaMenuSection[] = []

    const homeColumns: MegaMenuColumn[] = []
    const residentialLinks = topWithResults(residential, intentCategoryCount, 6).map((c) => ({
      label: c.name,
      href: buildPropertyHref(intent, {
        city: cityLower,
        category: c.name,
        categorySlug: c.slug || undefined,
      }),
    }))
    if (residentialLinks.length) {
      homeColumns.push({ heading: `Property Types in ${city}`, links: residentialLinks })
    }

    const popularSearchLinks: MegaMenuLink[] = []
    if (cityTotal > 0) {
      popularSearchLinks.push({
        label: `${isRent ? 'Rentals' : 'Property'} in ${city}`,
        href: buildPropertyHref(intent, { city: cityLower }),
      })
    }
    if ((live?.verified ?? 0) > 0) {
      popularSearchLinks.push({
        label: `QBC Verified in ${city}`,
        href: buildPropertyHref(intent, { city: cityLower, verified: 'true' }),
      })
    }
    for (const bhk of options.bhkOptions ?? []) {
      const digit = MENU_BHK_RE.exec(bhk.trim())?.[1]
      if (!digit || (live?.bhk?.[`${digit} BHK`] ?? 0) <= 0) continue
      popularSearchLinks.push({
        label: `${bhk} in ${city}`,
        href: buildPropertyHref(intent, { city: cityLower, bhk }),
      })
    }
    const newProject = isRent
      ? undefined
      : residential.find((c) => /new\s*project/i.test(c.name) && intentCategoryCount(c) > 0)
    if (newProject) {
      popularSearchLinks.push({
        label: `New Projects in ${city}`,
        href: buildPropertyHref(intent, {
          city: cityLower,
          category: 'New Project',
          categorySlug: newProject.slug || undefined,
        }),
      })
    }
    if (popularSearchLinks.length) {
      homeColumns.push({ heading: 'Popular Searches', links: popularSearchLinks })
    }
    if (homeColumns.length) {
      sections.push({
        id: isRent ? 'rent-home' : 'buy-home',
        label: isRent ? 'Rent a Home' : 'Buy a Home',
        columns: homeColumns,
      })
    }

    const areaColumns: MegaMenuColumn[] = []
    const areas = topWithResults(
      areasForCity(options.areas ?? [], city),
      (a) => (live ? live.areas?.[String(a.id)] ?? 0 : a.count ?? 0),
      6,
    )
    if (areas.length) {
      areaColumns.push({
        heading: `Popular Areas in ${city}`,
        links: areas.map((a) => ({
          label: titleCase(a.name),
          href: buildPropertyHref(intent, { city: cityLower, area: areaFilterValue(a) }),
        })),
      })
    }
    const otherCities = topWithResults(
      (options.cities ?? []).filter((c) => c.name.toLowerCase() !== cityLower),
      (c) => (live ? live.cities?.[String(c.id)] ?? 0 : c.count ?? 0),
      6,
    )
    if (otherCities.length) {
      areaColumns.push({
        heading: 'Explore Other Cities',
        links: otherCities.map((c) => ({
          label: titleCase(c.name),
          href: buildPropertyHref(intent, { city: c.name.toLowerCase() }),
        })),
      })
    }
    if (areaColumns.length) {
      sections.push({ id: 'popular-areas', label: 'Popular Areas', columns: areaColumns })
    }

    const commercialColumns: MegaMenuColumn[] = []
    const commercialLinks = topWithResults(commercial, countIn(commercialLive?.categories), 6)
    if (commercialLinks.length) {
      commercialColumns.push({
        heading: 'Commercial',
        links: commercialLinks.map((c) => ({
          label: c.name,
          href: buildPropertyHref('', {
            city: cityLower,
            category: c.name,
            categorySlug: c.slug || undefined,
          }),
        })),
      })
    }
    const landLinks = topWithResults(land, intentCategoryCount, 6)
    if (landLinks.length) {
      commercialColumns.push({
        heading: 'Land & Plots',
        links: landLinks.map((c) => ({
          label: c.name,
          href: buildPropertyHref(intent, { city: cityLower, category: c.name }),
        })),
      })
    }
    if (commercialColumns.length) {
      sections.push({ id: 'commercial', label: 'Commercial & Land', columns: commercialColumns })
    }

    // The menu is scoped to the selected city: no listings for this intent there
    // means no menu, even if other cities have some.
    if (cityTotal <= 0) sections.length = 0

    sections.push({
      id: 'articles',
      label: 'Articles & News',
      columns: [
        {
          heading: 'From QBC',
          links: [
            { label: 'All Articles', href: PATHS.blog },
            { label: 'Achievements', href: PATHS.achievements },
            { label: 'About QBC', href: PATHS.about },
          ],
        },
      ],
    })

    return {
      id: intent,
      promo: {
        variant: 'post-property',
        title: isRent ? 'Rent out your property faster!' : 'Sell at the right price!',
        subtitle: 'List your property now for FREE',
        cta: { label: 'Post Property', href: PATHS.addProperty },
      },
      sections,
    }
  }, [cityName, options, menuCounts, intent])
}
